/**
 * The Documents page's confirmation (#6559 round 19, Hunter): the same
 * shell as the page's other dialogs — Move, New folder, Rename — and the
 * design system's button grammar, so a delete reads as the rest of the
 * page rather than as the app-wide modal with its blue default. The one
 * action is the primary, ink on ink, as Move here and Create are; the
 * message states the consequence and nothing else.
 */
import React from "react";
import { Dialog } from "@mui/material";
import { Btn } from "@/components/FpPages/RMS/design/Button";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: () => void;
  busy?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel,
  onClose,
  onConfirm,
  busy = false,
}) => (
  <Dialog
    open={open}
    onClose={busy ? undefined : onClose}
    maxWidth="xs"
    fullWidth
    aria-labelledby="documents-confirm-title"
    slotProps={{
      paper: {
        // Phone gutters, as the Move dialog: the width, with a margin.
        sx: {
          margin: "16px",
          width: "calc(100% - 32px)",
          borderRadius: "12px",
          border: "1px solid var(--fp-border)",
          boxShadow: "var(--fp-lift)",
        },
      },
    }}
  >
    <div className="flex flex-col gap-3 p-4 md:p-5">
      <div
        id="documents-confirm-title"
        className="text-[15px] font-semibold text-[var(--fp-ink)]"
      >
        {title}
      </div>
      <p className="m-0 text-[13px] leading-5 text-[var(--fp-ink-2)]">
        {message}
      </p>
      <div className="flex items-center justify-end gap-2 pt-1">
        <Btn onClick={onClose} disabled={busy}>
          Cancel
        </Btn>
        {/* The one control that performs the action holds focus on open,
            so Enter confirms and Escape (the dialog's own) cancels. */}
        <Btn primary onClick={onConfirm} disabled={busy} autoFocus>
          {confirmLabel}
        </Btn>
      </div>
    </div>
  </Dialog>
);

export default ConfirmDialog;
